import { Store } from "@reduxjs/toolkit";

import { logout as logoutAction, signIn, updateAuthReady } from "../slice";
import {
  AuthConfig,
  DeviceAuthSetupOptions,
  IAuthProvider,
} from "../interfaces/IAuthProvider";
import {
  TV_ACCESS_TOKEN_KEY,
  TV_REFRESH_TOKEN_KEY,
  TV_TOKEN_EXPIRES_AT_KEY,
} from "../constants";

// Types
export interface DeviceCodeResponse {
  device_code: string;
  user_code: string;
  verification_uri: string;
  verification_uri_complete: string;
  expires_in: number;
  interval: number;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  scope?: string;
}

export interface DeviceAuthConfig {
  serverUrl: string;
  realm: string;
  clientId: string;
}

export type DeviceAuthStatus =
  | "idle"
  | "loading"
  | "ready"
  | "polling"
  | "success"
  | "error"
  | "expired";

export interface DeviceAuthState {
  status: DeviceAuthStatus;
  deviceCode: DeviceCodeResponse | null;
  tokens: TokenResponse | null;
  error: string | null;
  remainingTime: number;
}

// Internal helper to store tokens
function storeTvTokensInternal(tokens: {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}): void {
  localStorage.setItem(TV_ACCESS_TOKEN_KEY, tokens.access_token);
  localStorage.setItem(TV_REFRESH_TOKEN_KEY, tokens.refresh_token);
  localStorage.setItem(
    TV_TOKEN_EXPIRES_AT_KEY,
    String(Date.now() + tokens.expires_in * 1000),
  );
}

/**
 * DeviceAuthService handles the OAuth 2.0 Device Authorization Flow
 */
export class DeviceAuthService {
  private config: DeviceAuthConfig;
  private pollTimeout: ReturnType<typeof setTimeout> | null = null;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private currentInterval = 5000;
  private onStateChange: ((state: DeviceAuthState) => void) | null = null;

  private state: DeviceAuthState = {
    status: "idle",
    deviceCode: null,
    tokens: null,
    error: null,
    remainingTime: 0,
  };

  constructor(config: DeviceAuthConfig) {
    this.config = config;
  }

  private getBaseUrl(): string {
    const { serverUrl, realm } = this.config;
    const baseUrl = serverUrl.endsWith("/")
      ? serverUrl.slice(0, -1)
      : serverUrl;
    return `${baseUrl}/realms/${realm}/protocol/openid-connect`;
  }

  private updateState(updates: Partial<DeviceAuthState>): void {
    this.state = { ...this.state, ...updates };
    this.onStateChange?.(this.state);
  }

  subscribe(callback: (state: DeviceAuthState) => void): () => void {
    this.onStateChange = callback;
    callback(this.state);
    return () => {
      this.onStateChange = null;
    };
  }

  getState(): DeviceAuthState {
    return this.state;
  }

  async requestDeviceCode(): Promise<DeviceCodeResponse> {
    this.updateState({ status: "loading", error: null });

    try {
      const response = await fetch(`${this.getBaseUrl()}/auth/device`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: `client_id=${encodeURIComponent(this.config.clientId)}`,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data: DeviceCodeResponse = await response.json();
      this.currentInterval = (data.interval || 5) * 1000;

      this.updateState({
        status: "ready",
        deviceCode: data,
        remainingTime: data.expires_in,
      });

      return data;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to request device code";
      this.updateState({ status: "error", error: message });
      throw error;
    }
  }

  async pollToken(): Promise<TokenResponse | null> {
    const { deviceCode } = this.state;
    if (!deviceCode) {
      throw new Error(
        "No device code available. Call requestDeviceCode first.",
      );
    }

    try {
      const response = await fetch(`${this.getBaseUrl()}/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          client_id: this.config.clientId,
          device_code: deviceCode.device_code,
          grant_type: "urn:ietf:params:oauth:grant-type:device_code",
        }),
      });

      if (response.ok) {
        const tokens: TokenResponse = await response.json();
        storeTvTokensInternal(tokens);
        this.updateState({ status: "success", tokens });
        return tokens;
      }

      const error = await response.json();

      if (error.error === "authorization_pending") {
        return null;
      }

      if (error.error === "slow_down") {
        this.currentInterval += 5000;
        return null;
      }

      if (error.error === "expired_token") {
        this.updateState({ status: "expired", error: "Code expired" });
        throw new Error("Code expired");
      }

      const message =
        error.error_description || error.error || "Token request failed";
      this.updateState({ status: "error", error: message });
      throw new Error(message);
    } catch (error) {
      if (this.state.status !== "error" && this.state.status !== "expired") {
        console.error("Poll error:", error);
      }
      throw error;
    }
  }

  async start(): Promise<void> {
    await this.requestDeviceCode();
    this.startCountdown();
    this.startPolling();
  }

  private startCountdown(): void {
    this.stopCountdown();

    this.countdownInterval = setInterval(() => {
      const newTime = this.state.remainingTime - 1;

      if (newTime <= 0) {
        this.stopCountdown();
        this.stopPolling();
        this.updateState({
          status: "expired",
          remainingTime: 0,
          error: "Code expired",
        });
        return;
      }

      this.updateState({ remainingTime: newTime });
    }, 1000);
  }

  private startPolling(): void {
    this.updateState({ status: "polling" });

    const poll = async () => {
      if (
        this.state.status === "success" ||
        this.state.status === "error" ||
        this.state.status === "expired"
      ) {
        return;
      }

      try {
        const tokens = await this.pollToken();
        if (tokens) {
          this.stopCountdown();
          this.stopPolling();
          return;
        }
      } catch (error) {
        console.error("Poll error:", error);
      }

      this.pollTimeout = setTimeout(poll, this.currentInterval);
    };

    this.pollTimeout = setTimeout(poll, this.currentInterval);
  }

  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private stopPolling(): void {
    if (this.pollTimeout) {
      clearTimeout(this.pollTimeout);
      this.pollTimeout = null;
    }
  }

  reset(): void {
    this.stopCountdown();
    this.stopPolling();
    this.currentInterval = 5000;
    this.state = {
      status: "idle",
      deviceCode: null,
      tokens: null,
      error: null,
      remainingTime: 0,
    };
    this.onStateChange?.(this.state);
  }

  destroy(): void {
    this.reset();
    this.onStateChange = null;
  }
}

// Promise cache to prevent duplicate refresh requests
let refreshPromise: Promise<TokenResponse | null> | null = null;

/**
 * Clear all TV tokens from localStorage
 */
export function clearTvTokens(): void {
  localStorage.removeItem(TV_ACCESS_TOKEN_KEY);
  localStorage.removeItem(TV_REFRESH_TOKEN_KEY);
  localStorage.removeItem(TV_TOKEN_EXPIRES_AT_KEY);
}

/**
 * Refresh TV tokens using the refresh token
 */
async function refreshTvTokenInternal(
  config: AuthConfig,
): Promise<TokenResponse | null> {
  const refreshToken = localStorage.getItem(TV_REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    return null;
  }

  const baseUrl = config.serverUrl.endsWith("/")
    ? config.serverUrl.slice(0, -1)
    : config.serverUrl;
  const tokenEndpoint = `${baseUrl}/realms/${config.realm}/protocol/openid-connect/token`;

  try {
    const response = await fetch(tokenEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        client_id: config.clientId,
        grant_type: "refresh_token",
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      console.error("Failed to refresh TV token:", response.status);
      return null;
    }

    const tokens: TokenResponse = await response.json();
    storeTvTokensInternal(tokens);
    return tokens;
  } catch (error) {
    console.error("Error refreshing TV token:", error);
    return null;
  }
}

/**
 * Get the current TV access token synchronously (without refresh)
 */
export function getCurrentTvToken(): string {
  return localStorage.getItem(TV_ACCESS_TOKEN_KEY) || "";
}

/**
 * DeviceAuthProvider - Auth provider for TV/QR code login
 */
export class DeviceAuthProvider
  implements IAuthProvider<DeviceAuthSetupOptions>
{
  private config: AuthConfig;
  private store: Store | null = null;
  private deviceAuthService: DeviceAuthService | null = null;
  private qrCodeRedirectUrl: string | undefined = undefined;

  constructor(config: AuthConfig) {
    this.config = config;
    this.deviceAuthService = new DeviceAuthService(config);
  }

  async setup(
    options: DeviceAuthSetupOptions,
  ): Promise<{ authenticated: boolean }> {
    this.store = options.store;
    this.qrCodeRedirectUrl = options.qrCodeRedirectUrl;
    const dispatch = this.store.dispatch;

    // For device auth, we just mark as ready
    // The actual auth happens when user scans QR code
    dispatch(updateAuthReady(true));

    // Check if we already have a valid token
    const existingToken = getCurrentTvToken();
    if (existingToken) {
      dispatch(signIn());
      return { authenticated: true };
    }

    return { authenticated: false };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async login(redirectUri?: string): Promise<void> {
    if (!this.qrCodeRedirectUrl) return;

    const targetPlatform = import.meta.env.VITE_TARGET_PLATFORM;
    const isTV =
      ["tizen", "webos"].includes(targetPlatform) ||
      window?.AndroidBridge?.isTV();
    if (isTV) {
      window.history.pushState(null, "", this.qrCodeRedirectUrl);
      window.dispatchEvent(new PopStateEvent("popstate"));
    } else {
      window.location.href = this.qrCodeRedirectUrl;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async logout(ignoreDispatch = false): Promise<void> {
    clearTvTokens();
    this.store?.dispatch(logoutAction());
  }

  async getToken(): Promise<string | undefined> {
    const expiresAt = parseInt(
      localStorage.getItem(TV_TOKEN_EXPIRES_AT_KEY) || "0",
    );
    const currentTime = Date.now();

    // Refresh token if it expires within 30 seconds
    if (expiresAt - currentTime < 30000) {
      // Use promise caching to prevent duplicate refresh requests
      if (!refreshPromise) {
        refreshPromise = refreshTvTokenInternal(this.config).finally(() => {
          refreshPromise = null;
        });
      }
      const refreshed = await refreshPromise;
      if (refreshed) {
        return refreshed.access_token;
      }
    }

    return localStorage.getItem(TV_ACCESS_TOKEN_KEY) || undefined;
  }

  getCurrentToken(): string | undefined {
    const token = getCurrentTvToken();
    return token || undefined;
  }

  getClientId(): string {
    return this.config.clientId;
  }

  isInternalAccount(): boolean {
    return false;
  }

  getDeviceAuthService(): DeviceAuthService {
    if (!this.deviceAuthService) {
      this.deviceAuthService = new DeviceAuthService(this.config);
    }
    return this.deviceAuthService;
  }
}

/**
 * Create a device auth service from environment config
 */
export function createDeviceAuthService(
  configKey = "VITE_KC_AUTH_CONFIG",
): DeviceAuthService {
  const kcConfig = JSON.parse(import.meta.env[configKey] || "{}");
  return new DeviceAuthService({
    serverUrl: kcConfig.serverUrl,
    realm: kcConfig.realm,
    clientId: kcConfig.clientId,
  });
}
